// src/pages/technicians/AdminTechnicianDetailPage.tsx

import {
  AlertCircle,
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  Clock,
  Copy,
  Eye,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  RefreshCw,
  ShieldCheck,
  Ticket,
  User,
  UserCheck,
  X,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../components/ui/Toast";
import { fetchApp } from "../../services/apiService";

interface Technician {
  id: string;
  client_id: string;
  role_id: string;
  username: string;
  email?: string | null;
  external_access_token?: string | null;
  created_at?: string | null;
}

interface TicketAssignment {
  id: string;
  ticket_id: string;
  assigned_by: string;
  technician_id: string;
  assignment_type: string;
  status: string;
  assignment_note?: string | null;
  assigned_at?: string | null;
  accepted_at?: string | null;
  rejected_at?: string | null;
  rejection_reason?: string | null;
  completed_at?: string | null;
  is_current: boolean;
}

interface TicketOut {
  id: string;
  client_id: string;
  portal_account_id: string;
  ticket_number: string;
  subject: string;
  category?: string | null;
  priority: string;
  message: string;
  status: string;
  is_outsourced: boolean;
  resolved_at?: string | null;
  closed_at?: string | null;
  last_updated_at?: string | null;
  latitude?: string | number | null;
  longitude?: string | number | null;
  location_name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  assignments: TicketAssignment[];
  updates: unknown[];
}

interface TechnicianDetailResponse {
  technician: Technician;
  stats: Record<string, number>;
  assigned_tickets: TicketOut[];
}

const btnBase =
  "inline-flex items-center justify-center gap-2 rounded-2xl text-[13px] font-black transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.98]";

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleString("en-KE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatText = (value?: string | null) =>
  String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getStatusTone = (status?: string | null) => {
  const value = String(status || "").toLowerCase();

  if (value === "closed") return "border-slate-300 bg-slate-200 text-slate-700";
  if (value === "resolved" || value === "customer_confirmed")
    return "border-emerald-200 bg-emerald-100 text-emerald-800";
  if (
    value === "assigned" ||
    value === "accepted" ||
    value === "in_progress" ||
    value === "technician_completed"
  )
    return "border-sky-200 bg-sky-100 text-sky-800";
  if (value === "cancelled") return "border-rose-200 bg-rose-100 text-rose-700";

  return "border-amber-200 bg-amber-100 text-amber-800";
};

const getPriorityTone = (priority?: string | null) => {
  const value = String(priority || "").toLowerCase();

  if (value === "urgent" || value === "high")
    return "border-rose-200 bg-rose-100 text-rose-700";
  if (value === "medium") return "border-amber-200 bg-amber-100 text-amber-800";

  return "border-emerald-200 bg-emerald-100 text-emerald-800";
};

const maskToken = (token?: string | null) => {
  if (!token) return "No token";
  if (token.length <= 12) return token;
  return `${token.slice(0, 8)}••••••••${token.slice(-6)}`;
};

const getInitials = (name?: string | null) =>
  String(name || "T")
    .split(" ")
    .filter(Boolean)
    .map((item) => item[0])
    .join("")
    .toUpperCase()
    .slice(0, 2) || "T";

const StatusBadge: React.FC<{ status?: string | null }> = ({ status }) => (
  <span
    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-black ${getStatusTone(
      status,
    )}`}
  >
    <span className="h-1.5 w-1.5 rounded-full bg-current" />
    {formatText(status)}
  </span>
);

const PriorityBadge: React.FC<{ priority?: string | null }> = ({
  priority,
}) => (
  <span
    className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-black ${getPriorityTone(
      priority,
    )}`}
  >
    {formatText(priority)}
  </span>
);

const LoadingState: React.FC = () => (
  <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
    <div className="mx-auto flex min-h-[60vh] max-w-[1600px] items-center justify-center">
      <div className="w-full max-w-md overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

        <div className="relative overflow-hidden px-6 py-10 text-center">
          <div className="absolute -right-16 -top-20 h-52 w-52 rounded-full bg-emerald-200/35 blur-2xl" />
          <div className="absolute -bottom-20 left-4 h-52 w-52 rounded-full bg-cyan-200/35 blur-2xl" />

          <div className="relative mx-auto flex h-16 w-16 items-center justify-center rounded-3xl border border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-200/50">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
          </div>

          <p className="relative mt-4 text-[15px] font-black text-slate-900">
            Loading technician details…
          </p>

          <p className="relative mt-1 text-[12.5px] font-medium text-slate-500">
            Please wait while assigned ticket details are being prepared.
          </p>
        </div>
      </div>
    </div>
  </div>
);

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub: string;
  icon: React.ReactNode;
  tone: "slate" | "emerald" | "sky" | "amber" | "rose";
}> = ({ label, value, sub, icon, tone }) => {
  const styles = {
    slate: {
      card: "border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 shadow-slate-300/40",
      icon: "bg-slate-200 text-slate-700",
      text: "text-slate-600",
      bubble: "bg-slate-200/60",
    },
    emerald: {
      card: "border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 shadow-emerald-200/50",
      icon: "bg-emerald-100 text-emerald-700",
      text: "text-emerald-700",
      bubble: "bg-emerald-200/50",
    },
    sky: {
      card: "border-sky-200 bg-gradient-to-br from-sky-50 to-cyan-50 shadow-sky-200/50",
      icon: "bg-sky-100 text-sky-700",
      text: "text-sky-700",
      bubble: "bg-sky-200/50",
    },
    amber: {
      card: "border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 shadow-amber-200/50",
      icon: "bg-amber-100 text-amber-700",
      text: "text-amber-700",
      bubble: "bg-amber-200/50",
    },
    rose: {
      card: "border-rose-200 bg-gradient-to-br from-rose-50 to-red-50 shadow-rose-200/50",
      icon: "bg-rose-100 text-rose-700",
      text: "text-rose-700",
      bubble: "bg-rose-200/50",
    },
  }[tone];

  return (
    <div
      className={`relative overflow-hidden rounded-3xl border px-5 py-5 shadow-sm ${styles.card}`}
    >
      <div
        className={`absolute -right-10 -top-10 h-28 w-28 rounded-full ${styles.bubble}`}
      />

      <div className="relative flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl ${styles.icon}`}
        >
          {icon}
        </div>

        <div className="min-w-0">
          <p
            className={`text-[11px] font-black uppercase tracking-widest ${styles.text}`}
          >
            {label}
          </p>

          <p className="mt-0.5 truncate text-[22px] font-black tracking-tight text-slate-900 sm:text-3xl">
            {value}
          </p>

          <p className={`text-[11px] font-bold ${styles.text}`}>{sub}</p>
        </div>
      </div>
    </div>
  );
};

const DetailRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
    <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
      {icon}
    </div>

    <div className="min-w-0">
      <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
        {label}
      </p>

      <p className="mt-1 break-words text-[13px] font-black text-slate-900">
        {value}
      </p>
    </div>
  </div>
);

const TechnicianDetailPage: React.FC = () => {
  const { technicianId } = useParams<{ technicianId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [data, setData] = useState<TechnicianDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const technician = data?.technician;
  const tickets = data?.assigned_tickets || [];
  const stats = data?.stats || {};

  const loadTechnician = async (manual = false) => {
    if (!technicianId) {
      setError("Missing technician ID.");
      setLoading(false);
      return;
    }

    try {
      if (manual) setRefreshing(true);
      else setLoading(true);

      setError(null);

      const res = await fetchApp<TechnicianDetailResponse>(
        `/admin/technicians/${technicianId}`,
      );

      setData(res);
    } catch (err: any) {
      const msg = err?.message || "Failed to load technician details.";
      setError(msg);
      showToast(msg, "error");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadTechnician(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [technicianId]);

  const copyToken = async () => {
    if (!technician?.external_access_token) {
      showToast("No token available.", "error");
      return;
    }

    try {
      await navigator.clipboard.writeText(technician.external_access_token);
      showToast("Token copied to clipboard.", "success");
    } catch {
      showToast("Failed to copy token.", "error");
    }
  };

  const regenerateToken = async () => {
    if (!technician) return;

    const ok = window.confirm(
      `Regenerate external token for ${technician.username}? The old token will stop working.`,
    );

    if (!ok) return;

    try {
      setRegenerating(true);

      const updated = await fetchApp<Technician>(
        `/admin/technicians/${technician.id}/regenerate-token`,
        {
          method: "POST",
        },
      );

      setData((prev) =>
        prev
          ? {
              ...prev,
              technician: updated,
            }
          : prev,
      );

      showToast("Technician token regenerated successfully.", "success");
    } catch (err: any) {
      showToast(err?.message || "Failed to regenerate token.", "error");
    } finally {
      setRegenerating(false);
    }
  };

  const currentTickets = useMemo(() => {
    return tickets.filter(
      (item) =>
        !["closed", "cancelled"].includes(String(item.status).toLowerCase()),
    );
  }, [tickets]);

  if (loading && !data) {
    return <LoadingState />;
  }

  if (!technician) {
    return (
      <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
        <div className="mx-auto max-w-[900px] rounded-3xl border border-rose-200 bg-rose-50 p-6 text-rose-700 shadow-sm">
          <p className="font-black">Technician not found</p>
          <p className="mt-1 text-sm">
            {error || "The selected technician could not be loaded."}
          </p>

          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`${btnBase} mt-4 h-10 border border-rose-200 bg-white px-4 text-rose-700 hover:bg-rose-100`}
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#eef3f8] px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <div className="mx-auto max-w-[1600px] space-y-5 sm:space-y-6">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
          <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500" />

          <div className="relative overflow-hidden">
            <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full bg-emerald-200/35 blur-2xl" />
            <div className="absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-cyan-200/35 blur-2xl" />

            <div className="relative flex flex-col gap-4 p-4 sm:p-6 lg:flex-row lg:items-start lg:justify-between">
              <div className="min-w-0">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="mb-3 inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-slate-50 px-3 py-2 text-[12px] font-black text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Technicians
                </button>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10.5px] font-black uppercase tracking-[0.08em] text-emerald-700 sm:text-[11px]">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  Technician Detail
                </div>

                <div className="mt-4 flex items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl bg-emerald-100 text-[16px] font-black text-emerald-700 ring-1 ring-emerald-200">
                    {getInitials(technician.username)}
                  </div>

                  <div className="min-w-0">
                    <h1 className="truncate text-[22px] font-black tracking-[-0.5px] text-slate-900 sm:text-[24px]">
                      {technician.username}
                    </h1>

                    <p className="mt-1 text-[13px] font-medium text-slate-500">
                      View technician profile, token, and currently assigned
                      tickets.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={() => loadTechnician(true)}
                  disabled={refreshing}
                  className={`${btnBase} h-10 border border-slate-300 bg-slate-50 px-4 text-slate-600 shadow-sm hover:bg-slate-100 hover:text-slate-800`}
                >
                  {refreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Refresh
                </button>

                <button
                  type="button"
                  onClick={regenerateToken}
                  disabled={regenerating}
                  className={`${btnBase} h-10 bg-gradient-to-r from-emerald-500 to-teal-500 px-5 text-white shadow-lg shadow-emerald-500/20 hover:from-emerald-600 hover:to-teal-600`}
                >
                  {regenerating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <KeyRound className="h-4 w-4" />
                  )}
                  Regenerate Token
                </button>
              </div>
            </div>
          </div>
        </div>

        {refreshing && (
          <div className="flex items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-bold text-emerald-800 shadow-sm shadow-emerald-200/40">
            <Loader2 className="h-4 w-4 animate-spin" />
            Refreshing technician details…
          </div>
        )}

        {error && (
          <div className="flex items-start justify-between gap-3 rounded-3xl border border-rose-200 bg-rose-50 px-4 py-3.5 text-[13px] font-bold text-rose-700 shadow-sm shadow-rose-200/40">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <p>{error}</p>
            </div>

            <button
              type="button"
              onClick={() => setError(null)}
              className="rounded-xl p-1.5 text-rose-600 transition-colors hover:bg-rose-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5 xl:gap-4">
          <StatCard
            label="Total Assigned"
            value={stats.total_assigned || 0}
            sub="All assigned tickets"
            icon={<Ticket className="h-5 w-5" />}
            tone="slate"
          />

          <StatCard
            label="Active"
            value={currentTickets.length}
            sub="Not closed/cancelled"
            icon={<UserCheck className="h-5 w-5" />}
            tone="sky"
          />

          <StatCard
            label="In Progress"
            value={stats.in_progress || 0}
            sub="Technician working"
            icon={<Clock className="h-5 w-5" />}
            tone="amber"
          />

          <StatCard
            label="Completed"
            value={stats.technician_completed || 0}
            sub="Completed by technician"
            icon={<CheckCircle2 className="h-5 w-5" />}
            tone="emerald"
          />

          <StatCard
            label="Closed"
            value={stats.closed || 0}
            sub="Closed tickets"
            icon={<ShieldCheck className="h-5 w-5" />}
            tone="slate"
          />
        </div>

        <div className="grid grid-cols-1 gap-5 xl:grid-cols-[420px_1fr]">
          <div className="space-y-5">
            <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
              <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
                <p className="text-[14px] font-black text-slate-900">
                  Technician Profile
                </p>

                <p className="text-[11.5px] font-medium text-slate-500">
                  Account and access details.
                </p>
              </div>

              <div className="space-y-3 p-4 sm:p-5">
                <DetailRow
                  icon={<User className="h-4 w-4" />}
                  label="Username"
                  value={technician.username}
                />

                <DetailRow
                  icon={<Mail className="h-4 w-4" />}
                  label="Email"
                  value={technician.email || "—"}
                />

                <DetailRow
                  icon={<CalendarDays className="h-4 w-4" />}
                  label="Created"
                  value={formatDateTime(technician.created_at)}
                />

                <div className="rounded-3xl border border-slate-200 bg-slate-100 px-4 py-4">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    External Access Token
                  </p>

                  <p className="mt-2 break-all font-mono text-[12px] font-black text-slate-900">
                    {maskToken(technician.external_access_token)}
                  </p>

                  <button
                    type="button"
                    onClick={copyToken}
                    className={`${btnBase} mt-3 h-9 border border-slate-300 bg-slate-50 px-3 text-slate-600 hover:bg-slate-100 hover:text-slate-800`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy Full Token
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50/95 shadow-xl shadow-slate-300/30">
            <div className="border-b border-slate-200 bg-slate-100/80 px-4 py-4 sm:px-5">
              <p className="text-[14px] font-black text-slate-900">
                Assigned Tickets
              </p>

              <p className="text-[11.5px] font-medium text-slate-500">
                Tickets currently assigned to this technician.
              </p>
            </div>

            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full text-left text-[13px]">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-100 text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-5 py-3.5 font-black">Ticket</th>
                    <th className="px-5 py-3.5 font-black">Subject</th>
                    <th className="px-5 py-3.5 font-black">Priority</th>
                    <th className="px-5 py-3.5 font-black">Status</th>
                    <th className="px-5 py-3.5 font-black">Location</th>
                    <th className="px-5 py-3.5 font-black">Created</th>
                    <th className="px-5 py-3.5 text-right font-black">
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-200/70 bg-slate-50">
                  {tickets.length > 0 ? (
                    tickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        className="cursor-pointer transition-colors hover:bg-emerald-50/60"
                        onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                      >
                        <td className="px-5 py-4 font-mono text-[12.5px] font-black text-slate-900">
                          {ticket.ticket_number}
                        </td>

                        <td className="max-w-[340px] px-5 py-4">
                          <p className="truncate text-[13px] font-black text-slate-900">
                            {ticket.subject}
                          </p>

                          <p className="mt-0.5 line-clamp-1 text-[11.5px] font-medium text-slate-500">
                            {ticket.message}
                          </p>
                        </td>

                        <td className="px-5 py-4">
                          <PriorityBadge priority={ticket.priority} />
                        </td>

                        <td className="px-5 py-4">
                          <StatusBadge status={ticket.status} />
                        </td>

                        <td className="px-5 py-4">
                          <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                            <MapPin className="h-3 w-3" />
                            {ticket.location_name || "No location"}
                          </span>
                        </td>

                        <td className="px-5 py-4 text-[12px] font-medium text-slate-500">
                          {formatDateTime(ticket.created_at)}
                        </td>

                        <td className="px-5 py-4 text-right">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/support/tickets/${ticket.id}`);
                            }}
                            className={`${btnBase} h-9 border border-sky-200 bg-sky-100 px-3 text-sky-800 hover:bg-sky-200`}
                          >
                            <Eye className="h-3.5 w-3.5" />
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-5 py-16 text-center">
                        <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                          <Ticket className="h-7 w-7 text-slate-500" />
                        </div>

                        <p className="text-[14px] font-black text-slate-800">
                          No assigned tickets
                        </p>

                        <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                          Tickets assigned to this technician will appear here.
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="divide-y divide-slate-200 bg-slate-50 md:hidden">
              {tickets.length > 0 ? (
                tickets.map((ticket) => (
                  <button
                    key={ticket.id}
                    type="button"
                    onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                    className="w-full px-4 py-4 text-left transition-colors hover:bg-emerald-50"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-mono text-[12.5px] font-black text-slate-900">
                          {ticket.ticket_number}
                        </p>

                        <p className="mt-1 truncate text-[14px] font-black text-slate-900">
                          {ticket.subject}
                        </p>

                        <p className="mt-1 line-clamp-2 text-[12px] font-medium leading-relaxed text-slate-500">
                          {ticket.message}
                        </p>
                      </div>

                      <StatusBadge status={ticket.status} />
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <PriorityBadge priority={ticket.priority} />

                      <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-200 px-2.5 py-1 text-[11px] font-black text-slate-600">
                        <MapPin className="h-3 w-3" />
                        {ticket.location_name || "No location"}
                      </span>
                    </div>

                    <div className="mt-4 flex justify-end">
                      <span
                        className={`${btnBase} h-9 border border-sky-200 bg-sky-100 px-3 text-sky-800`}
                      >
                        <Eye className="h-3.5 w-3.5" />
                        View Ticket
                      </span>
                    </div>
                  </button>
                ))
              ) : (
                <div className="px-4 py-16 text-center">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-3xl bg-slate-200">
                    <Ticket className="h-7 w-7 text-slate-500" />
                  </div>

                  <p className="text-[14px] font-black text-slate-800">
                    No assigned tickets
                  </p>

                  <p className="mx-auto mt-1 max-w-sm text-[13px] font-medium text-slate-500">
                    Tickets assigned to this technician will appear here.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicianDetailPage;
